/**
 * Ruteo por ROL de toda la app. Fuente única de "a qué panel va esta persona".
 *
 * Nació para el micrositio (SiteHeader/SiteFooter mandaban a `/dashboard` sin
 * mirar el rol, o sea al panel del PRODUCTOR, y perdían el slug del remate por
 * el camino), pero hoy lo consumen también los cuatro guards de `App.jsx`
 * (ClienteRoute, PujaRoute, PublicRoute, el catch-all de 404), `Login.jsx` y
 * `Register.jsx`.
 *
 * Si vas a agregar un rol o un panel, se toca ACÁ y en ningún otro lado. La
 * primera versión de este archivo se escribió justamente porque la decisión
 * estaba copiada en cinco lugares y en varios estaba mal.
 */

/**
 * Panel que le corresponde a cada rol.
 *
 * OJO con la segunda línea: **rol 1 SIN `id_clientes` = admin de plataforma
 * (Puja)**. Un rol 1 CON cabaña es un administrador de esa cabaña y va al panel
 * del productor. Esa distinción es una regla de negocio central del
 * multi-tenant, no un detalle de ruteo: ver AGENTS.md § Roles y paneles.
 */
/**
 * Rol 1 SIN cabaña = admin de la plataforma. CON cabaña es admin de esa cabaña.
 * El discriminante es la ausencia de `id_clientes`, no el rol.
 */
export const esAdminPlataforma = (user) => user?.id_rol === 1 && !user?.id_clientes;

/** Comprador. No pertenece a ninguna cabaña: pide acceso por cabaña. */
export const esComprador = (user) => user?.id_rol === 3;

export const rutaPanel = (user) => {
  if (esComprador(user)) return '/cliente/dashboard';
  if (esAdminPlataforma(user)) return '/puja/tickets';
  return '/dashboard';                                                  // productor / admin de cabaña
};

/**
 * Login arrastrando el remate del que viene la persona.
 *
 * El backend resuelve ese slug contra `Evento.slug` para dejarle pedida la
 * autorización a la cabaña cuando se registra. Si el slug se pierde acá, el
 * circuito micrositio → registro → solicitud queda desconectado y la persona
 * termina en un panel vacío sin entender por qué no puede ofertar.
 */
export const rutaLogin = (slug) =>
  slug ? `/login?evento=${encodeURIComponent(slug)}` : '/login';

/**
 * Alta arrastrando el remate. Espejo exacto de `rutaLogin`: los dos lados del
 * cruce login <-> registro tienen que perder o conservar el slug IGUAL. Cuando
 * uno de los dos se armaba a mano, la mitad del circuito lo perdia.
 */
export const rutaRegistro = (slug) =>
  slug ? `/registro?evento=${encodeURIComponent(slug)}` : '/registro';

/**
 * Remates del comprador, arrastrando el remate del que viene.
 *
 * Lo usan los TRES lugares que deciden donde cae alguien despues de
 * autenticarse con un slug en la mano: `Login` (comprador que entra desde un
 * micrositio), `Register` (alta recien hecha) y el modal de bienvenida de
 * `ClienteLayout`. Estaba copiado en los tres, dos de ellos caracter por
 * caracter — que es exactamente lo que este archivo existe para evitar.
 */
export const rutaRemates = (slug) =>
  slug ? `/cliente/remates?evento=${encodeURIComponent(slug)}` : '/cliente/remates';

/**
 * A donde cae alguien DESPUES de autenticarse, con o sin remate en la mano.
 *
 * Login y Register hacian esta misma cuenta cada uno por su lado, y Register se
 * salteaba el `esComprador`. Hoy no rompe —el alta publica siempre crea rol 3 y
 * el backend fuerza el `id_rol`— pero es correcto por una invariante del
 * backend, no por la logica del archivo: el dia que /auth/registro acepte otro
 * rol, Register manda un productor a /cliente/remates y Login no. La pregunta
 * "hay slug Y esta persona es compradora?" es UNA, asi que se responde una vez.
 */
export const rutaPostAuth = (user, slug) =>
  slug && esComprador(user) ? rutaRemates(slug) : rutaPanel(user);

/** Destino del botón de acceso: panel si ya entró, login si no. */
export const rutaAcceso = (isAuthenticated, user, slug) =>
  isAuthenticated ? rutaPanel(user) : rutaLogin(slug);
